function serviceAnim(options) {
  // Resolve dependencies and accessibility preferences.
  var $ = (options && options.jQuery) || window.jQuery;
  var gsapInstance = (options && options.gsapInstance) || window.gsap;
  var scrollTriggerInstance =
    (options && options.scrollTriggerInstance) || window.ScrollTrigger;
  var prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (!$ || !gsapInstance || !scrollTriggerInstance) {
    return;
  }

  function getFirstMatch(selectors) {
    var match = $();

    selectors.some(function (selector) {
      match = $(selector).first();
      return match.length > 0;
    });

    return match;
  }

  // Support both the new SVG classes and the legacy IDs.
  var backgroundWrapper = getFirstMatch([".base", "#base"]);
  var firstComponent = getFirstMatch([".first-element"]);
  var secondComponent = getFirstMatch([".second-element"]);
  var thirdComponent = getFirstMatch([".third-element"]);
  var fourthComponent = getFirstMatch([".fourth-element"]);
  var fifthComponent = getFirstMatch([".fifth-element"]);

  var contentTargets = [
    firstComponent.get(0),
    secondComponent.get(0),
    thirdComponent.get(0),
    fourthComponent.get(0),
    fifthComponent.get(0),
  ].filter(function (element, index, elements) {
    return element && elements.indexOf(element) === index;
  });

  var allTargets = contentTargets.slice();

  var goUpTargets = [
    {
      target: firstComponent.get(0),
      y: -110,
      duration: 1.35,
      entryOffset: 0,
      exitOffset: 0,
    },
    {
      target: secondComponent.get(0),
      y: -190,
      duration: 1.75,
      entryOffset: 0.18,
      exitOffset: 0.22,
    },
    {
      target: thirdComponent.get(0),
      y: -95,
      duration: 0.95,
      entryOffset: 0.08,
      exitOffset: 0.38,
    },
    {
      target: fourthComponent.get(0),
      y: -95,
      duration: 1.12,
      entryOffset: 0.12,
      exitOffset: 0.30,
    },
    {
      target: fifthComponent.get(0),
      y: -95,
      duration: 1.04,
      entryOffset: 0.07,
      exitOffset: 0.28,
    },
  ].filter(function (item) {
    return !!item.target;
  });

  // Keep the entrance direct and smooth, without an overshoot/bounce.
  var entryStartScale = 1;
  var entryStartY = 1;
  var entryDuration = 0.6;
  var entryLeadDelay = 0.3;
  var loopRepeatDelay = 0;
  var exitDurationMultiplier = 0.5;
  var goUpDelay = 0.5;
  var entryDurationMin = entryDuration * 0.5;
  var entryDurationMax = entryDuration * 1.55;
  var pairEntrySpread = 0.22;
  var allTogetherEntrySpread = 0.32;
  var lateEntryDelayMin = 0.75;
  var lateEntryDelayMax = 2.45;
  var scatteredEntryWindow = 2.75;
  var earlyHoldMin = goUpDelay * 0.35;
  var earlyHoldMax = goUpDelay * 1.55;
  var mediumHoldMin = goUpDelay * 1.15;
  var mediumHoldMax = goUpDelay * 3.8;
  var longHoldMin = goUpDelay * 2.4;
  var longHoldMax = goUpDelay * 5.6;
  var cycleEndGapMin = 0.12;
  var cycleEndGapMax = 0.9;
  var randomEdgePadding = 24;
  var mobileViewportMax = 567;
  var mobileRandomEdgePaddingRatio = 0.18;
  var randomGap = 0;
  var randomAttempts = 240;
  var randomFallbackSteps = 12;
  var triggerElement = backgroundWrapper.get(0) || contentTargets[0];

  if (!triggerElement) {
    return;
  }

  gsapInstance.registerPlugin(scrollTriggerInstance);

  function getTargetBaseBox(target) {
    if (!target) {
      return null;
    }

    if (target.__smmBaseBox) {
      return target.__smmBaseBox;
    }

    if (typeof target.getBBox === "function") {
      try {
        target.__smmBaseBox = target.getBBox();
        return target.__smmBaseBox;
      } catch (error) {
        return null;
      }
    }

    return null;
  }

  function getSvgBounds(target) {
    var svg = target && target.ownerSVGElement;

    if (!svg) {
      return null;
    }

    if (svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.width) {
      return {
        x: svg.viewBox.baseVal.x,
        y: svg.viewBox.baseVal.y,
        width: svg.viewBox.baseVal.width,
        height: svg.viewBox.baseVal.height,
      };
    }

    return {
      x: 0,
      y: 0,
      width: parseFloat(svg.getAttribute("width")) || 0,
      height: parseFloat(svg.getAttribute("height")) || 0,
    };
  }

  function isMobileViewport() {
    return window.innerWidth < mobileViewportMax;
  }

  function getPlacementEdgePadding(svgBounds, box) {
    var edgePadding = randomEdgePadding;

    if (!isMobileViewport() || !svgBounds || !box) {
      return edgePadding;
    }

    edgePadding = Math.max(
      edgePadding,
      Math.min(svgBounds.width, svgBounds.height) *
        mobileRandomEdgePaddingRatio,
    );

    return Math.min(
      edgePadding,
      Math.max(0, (svgBounds.width - box.width) / 2),
      Math.max(0, (svgBounds.height - box.height) / 2),
    );
  }

  function getRandomBetween(min, max) {
    if (max <= min) {
      return min;
    }

    return min + Math.random() * (max - min);
  }

  function getRandomInt(max) {
    return Math.floor(getRandomBetween(0, max));
  }

  function getRandomizedDuration(baseDuration, jitter) {
    return Math.max(0.2, baseDuration + getRandomBetween(-jitter, jitter));
  }

  function shuffleItems(items) {
    var shuffledItems = items.slice();
    var index = shuffledItems.length - 1;

    while (index > 0) {
      var randomIndex = getRandomInt(index + 1);
      var item = shuffledItems[index];

      shuffledItems[index] = shuffledItems[randomIndex];
      shuffledItems[randomIndex] = item;
      index -= 1;
    }

    return shuffledItems;
  }

  function boxesOverlap(firstBox, secondBox, gap) {
    return !(
      firstBox.x + firstBox.width + gap <= secondBox.x ||
      secondBox.x + secondBox.width + gap <= firstBox.x ||
      firstBox.y + firstBox.height + gap <= secondBox.y ||
      secondBox.y + secondBox.height + gap <= firstBox.y
    );
  }

  function hasPlacementOverlap(candidateBox, placedBoxes) {
    return placedBoxes.some(function (placedBox) {
      return boxesOverlap(candidateBox, placedBox, randomGap);
    });
  }

  function getEvenlySpacedValues(min, max, steps) {
    var values = [];

    if (max <= min) {
      return [min];
    }

    for (var step = 0; step <= steps; step += 1) {
      values.push(min + (max - min) * (step / steps));
    }

    return values;
  }

  function assignRandomPlacements() {
    var svgBounds = getSvgBounds(contentTargets[0]);

    if (!svgBounds) {
      contentTargets.forEach(function (target) {
        target.__smmPlacement = {
          x: 0,
          y: 0,
        };
      });
      return;
    }

    var placedBoxes = [];
    var targetsByArea = contentTargets
      .map(function (target) {
        var box = getTargetBaseBox(target);

        return {
          target: target,
          box: box,
          area: box ? box.width * box.height : 0,
        };
      })
      .sort(function (firstItem, secondItem) {
        return secondItem.area - firstItem.area;
      });

    targetsByArea.forEach(function (item) {
      var box = item.box;
      var target = item.target;
      var placement = {
        x: 0,
        y: 0,
      };

      if (!box) {
        target.__smmPlacement = placement;
        return;
      }

      var edgePadding = getPlacementEdgePadding(svgBounds, box);
      var minX = svgBounds.x + edgePadding;
      var minY = svgBounds.y + edgePadding;
      var maxX = svgBounds.x + svgBounds.width - box.width - edgePadding;
      var maxY = svgBounds.y + svgBounds.height - box.height - edgePadding;
      var resolved = false;
      var attempt = 0;

      while (attempt < randomAttempts && !resolved) {
        var candidateX = getRandomBetween(minX, maxX);
        var candidateY = getRandomBetween(minY, maxY);
        var candidateBox = {
          x: candidateX,
          y: candidateY,
          width: box.width,
          height: box.height,
        };

        if (!hasPlacementOverlap(candidateBox, placedBoxes)) {
          placement = {
            x: candidateX - box.x,
            y: candidateY - box.y,
          };
          placedBoxes.push(candidateBox);
          resolved = true;
        }

        attempt += 1;
      }

      if (!resolved) {
        var fallbackXs = getEvenlySpacedValues(minX, maxX, randomFallbackSteps);
        var fallbackYs = getEvenlySpacedValues(minY, maxY, randomFallbackSteps);

        fallbackYs.some(function (candidateY) {
          return fallbackXs.some(function (candidateX) {
            var candidateBox = {
              x: candidateX,
              y: candidateY,
              width: box.width,
              height: box.height,
            };

            if (hasPlacementOverlap(candidateBox, placedBoxes)) {
              return false;
            }

            placement = {
              x: candidateX - box.x,
              y: candidateY - box.y,
            };
            placedBoxes.push(candidateBox);
            resolved = true;
            return true;
          });
        });
      }

      if (!resolved) {
        placement = {
          x: 0,
          y: 0,
        };
      }

      target.__smmPlacement = placement;
    });
  }

  function getTargetPlacement(target) {
    if (!target || !target.__smmPlacement) {
      return {
        x: 0,
        y: 0,
      };
    }

    return target.__smmPlacement;
  }

  function setElementCenterOrigin(target) {
    if (!target) {
      return;
    }

    var box = getTargetBaseBox(target);
    var placement = getTargetPlacement(target);

    if (box && box.width >= 0 && box.height >= 0) {
      gsapInstance.set(target, {
        svgOrigin:
          box.x +
          placement.x +
          box.width / 2 +
          " " +
          (box.y + placement.y + box.height / 2),
        transformOrigin: "50% 50%",
      });
      return;
    }

    gsapInstance.set(target, {
      transformBox: "fill-box",
      transformOrigin: "center center",
    });
  }

  function setInitialState() {
    assignRandomPlacements();

    contentTargets.forEach(function (target) {
      var placement = getTargetPlacement(target);

      setElementCenterOrigin(target);
      gsapInstance.set(target, {
        opacity: 0,
        x: placement.x,
        scale: entryStartScale,
        y: placement.y + entryStartY,
      });
    });
  }

  // Reduced motion: reveal the final state without transitions.
  if (prefersReducedMotion) {
    assignRandomPlacements();

    allTargets.forEach(function (target) {
      var placement = getTargetPlacement(target);

      setElementCenterOrigin(target);
      gsapInstance.set(target, {
        opacity: 1,
        x: placement.x,
        scale: 1,
        y: placement.y,
      });
    });
    return;
  }

  function getRandomCycleSchedule() {
    var shuffledTargets = shuffleItems(goUpTargets);
    var mode = getRandomBetween(0, 1);
    var baseStart = entryLeadDelay + getRandomBetween(0, 0.35);
    var quickPairIndex = getRandomInt(Math.min(2, shuffledTargets.length));
    var lingeringIndex = getRandomInt(shuffledTargets.length);
    var schedule = [];

    if (mode < 0.36) {
      shuffledTargets.forEach(function (item, index) {
        var isPair = index < 2;
        var isQuickPairItem = isPair && index === quickPairIndex;

        schedule.push({
          item: item,
          entryStart:
            baseStart +
            (isPair
              ? getRandomBetween(0, pairEntrySpread)
              : getRandomBetween(lateEntryDelayMin, lateEntryDelayMax)),
          holdMin: isQuickPairItem ? earlyHoldMin : mediumHoldMin,
          holdMax: isQuickPairItem ? earlyHoldMax : longHoldMax,
        });
      });
      return schedule;
    }

    if (mode < 0.68) {
      shuffledTargets.forEach(function (item, index) {
        var lingers = index === lingeringIndex;

        schedule.push({
          item: item,
          entryStart: baseStart + getRandomBetween(0, allTogetherEntrySpread),
          holdMin: lingers ? longHoldMin : earlyHoldMin,
          holdMax: lingers ? longHoldMax : earlyHoldMax,
        });
      });
      return schedule;
    }

    shuffledTargets.forEach(function (item) {
      schedule.push({
        item: item,
        entryStart: baseStart + getRandomBetween(0, scatteredEntryWindow),
        holdMin: earlyHoldMin,
        holdMax: longHoldMax,
      });
    });

    return schedule;
  }

  function addTargetAnimation(timeline, scheduleItem) {
    var item = scheduleItem.item;
    var entryDurationForItem = getRandomBetween(
      entryDurationMin,
      entryDurationMax,
    );
    var exitDuration = item.duration * exitDurationMultiplier;
    var exitDurationForItem = getRandomizedDuration(
      exitDuration,
      exitDuration * 0.55,
    );
    var holdDuration =
      getRandomBetween(scheduleItem.holdMin, scheduleItem.holdMax) +
      item.exitOffset * getRandomBetween(0.2, 1.4);
    var exitStart =
      scheduleItem.entryStart + entryDurationForItem + holdDuration;

    timeline.to(
      item.target,
      {
        opacity: 1,
        x: function (tweenIndex, target) {
          return getTargetPlacement(target).x;
        },
        scale: 1,
        y: function (tweenIndex, target) {
          return getTargetPlacement(target).y;
        },
        duration: entryDurationForItem,
        ease: "power2.out",
      },
      scheduleItem.entryStart + item.entryOffset * getRandomBetween(0, 0.8),
    );

    timeline.to(
      item.target,
      {
        x: function (index, target) {
          return getTargetPlacement(target).x;
        },
        y: function (index, target) {
          return getTargetPlacement(target).y + item.y;
        },
        opacity: 0,
        duration: exitDurationForItem,
        ease: "power3.in",
      },
      exitStart,
    );

    return exitStart + exitDurationForItem;
  }

  function populateRandomCycle(timeline) {
    var cycleEnd = 0;

    getRandomCycleSchedule().forEach(function (scheduleItem) {
      cycleEnd = Math.max(cycleEnd, addTargetAnimation(timeline, scheduleItem));
    });

    timeline.add(
      function () {},
      cycleEnd + getRandomBetween(cycleEndGapMin, cycleEndGapMax),
    );
  }

  function playRandomCycle() {
    var timeline;

    setInitialState();
    timeline = gsapInstance.timeline({
      delay: loopRepeatDelay,
      onComplete: playRandomCycle,
    });

    populateRandomCycle(timeline);
  }

  setInitialState();

  scrollTriggerInstance.create({
    trigger: triggerElement,
    start: "top 85%",
    once: true,
    onEnter: playRandomCycle,
  });
}

module.exports = serviceAnim;
